import React, { useState, useEffect } from 'react';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const WithdrawalRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchParams, setSearchParams] = useState({
    status: 'all',
    startDate: null,
    endDate: null
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(searchParams.status !== 'all' && { status: searchParams.status }),
        ...(searchParams.startDate && { startDate: searchParams.startDate.toISOString() }),
        ...(searchParams.endDate && { endDate: searchParams.endDate.toISOString() })
      });

      const response = await sendApiRequest(`api/withdrawal-requests/user?${params}`, {}, 'GET');
      
      if (response.status === 200) {
        setRequests(response.data.withdrawalRequests || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalRecords(response.data.total || 0);
      } else {
        toast.error(response.data?.message || 'Failed to fetch withdrawal requests');
        setRequests([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error(error.message || 'An error occurred while fetching requests');
      setRequests([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [currentPage, itemsPerPage]);

  const handleSearch = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    setSearchParams(prev => ({ ...prev, [field]: date }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchRequests();
  };

  const handleReset = () => {
    setSearchParams({
      status: 'all',
      startDate: null,
      endDate: null
    });
    setCurrentPage(1);
    fetchRequests();
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">My Withdrawal Requests</h1>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <form onSubmit={handleSearchSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={searchParams.status}
                onChange={handleSearch}
                className="w-full p-2 border rounded-md"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <DatePicker
                selected={searchParams.startDate}
                onChange={(date) => handleDateChange(date, 'startDate')}
                className="w-full p-2 border rounded-md"
                dateFormat="yyyy-MM-dd"
                placeholderText="Select start date"
                maxDate={searchParams.endDate || new Date()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <DatePicker
                selected={searchParams.endDate}
                onChange={(date) => handleDateChange(date, 'endDate')}
                className="w-full p-2 border rounded-md"
                dateFormat="yyyy-MM-dd"
                placeholderText="Select end date"
                minDate={searchParams.startDate}
                maxDate={new Date()}
              />
            </div>
            <div className="flex items-end space-x-2">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Reset
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-gray-600">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
          {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Note</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    No withdrawal requests found
                  </td>
                </tr>
              ) : (
                requests.map((request, index) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {((currentPage - 1) * itemsPerPage) + index + 1}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          Requested: ₹{request.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-gray-500">
                          Admin Fee (5%): ₹{request.deductions.adminFee.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-gray-500">
                          TDS (5%): ₹{request.deductions.tds.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-sm font-semibold text-green-600">
                          Net Amount: ₹{request.deductions.netAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Bank Name:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.bankName}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Account No:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.accountNumber}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">IFSC Code:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.ifscCode}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Branch:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.branchName}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500 w-24">Type:</span>
                            <span className="font-medium text-gray-900">{request.bankDetails?.accountType}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          request.status === 'approved'
                            ? 'bg-green-100 text-green-800' 
                            : request.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {request.adminNote || 'No note provided'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-between items-center">
        <div className="flex items-center">
          <span className="mr-2">Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="p-1 border rounded"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="ml-2">entries</span>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequestList;